// Enums and primitive type aliases

export enum EmbeddingJobType {
  EmbedJobs           = 'embed-jobs',
  EmbedProfile        = 'embed-profile',
  EmbedBatchJobs      = 'embed-batch-jobs',
  EmbedBatchProfiles  = 'embed-batch-profiles',
  Backfill            = 'backfill',
  MigrateModel        = 'migrate-model',
}
// User / Employee
export enum EmployeeRole {
  JobScout = 'job_scout',
  TeamLead = 'team_lead',
  Manager = 'manager',
  Admin = 'admin',
}

export enum Role {
  Admin = 'admin',
  Manager = 'manager',
  TeamLead = 'team_lead',
  JobScout = 'job_scout',
  User = 'user',
  Customer = 'customer',
}

export type UserType = 'employee' | 'customer';
export type UserStatus = 'active' | 'inactive';
export type EmployeeStatus = 'active' | 'inactive';
export type GoalStatus = 'on-track' | 'at-risk' | 'completed';
export type PerformancePeriod = 'week' | 'month' | 'quarter' | 'year';
export type TrendDirection = 'up' | 'down' | 'stable';

// Jobs
export enum JobType {
  FullTime = 'full-time',
  PartTime = 'part-time',
  Contract = 'contract',
  Temporary = 'temporary',
  Internship = 'internship',
  PerDiem = 'per-diem',
  Volunteer = 'volunteer',
}

export enum Platform {
  LinkedIn = 'linkedin',
  Indeed = 'indeed',
  ZipRecruiter = 'ziprecruiter',
  Bayt = 'bayt',
  Naukri = 'naukri',
  Google = 'google',
  Generic = 'generic',
}

export enum CompensationInterval {
  Yearly = 'yearly',
  Monthly = 'monthly',
  Weekly = 'weekly',
  Daily = 'daily',
  Hourly = 'hourly',
}

export enum DescriptionFormat {
  Markdown = 'markdown',
  Html = 'html',
  Plain = 'plain',
}

export enum WorkMode {
  Remote = 'remote',
  Hybrid = 'hybrid',
  Onsite = 'onsite',
}

export enum JobStatus {
  Draft = 'draft',
  Active = 'active',
  Expired = 'expired',
}

export type ScrapingStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'retrying';
export enum DuplicateStatus {
  Unresolved = 'unresolved',
  Resolved = 'resolved',
  NotDuplicate = 'not_duplicate',
}
export enum TrackingStatus {
  Interested   = 'interested',
  Applied      = 'applied',
  Interviewing = 'interviewing',
  Rejected     = 'rejected',
  Offer        = 'offer',
  Declined     = 'declined',
}
export type AssignmentType = 'automatic' | 'manual';
export type RecommendationStatus = 'pending' | 'viewed' | 'saved' | 'applied' | 'dismissed';

// Applications / Customers
export type ApplicationStatus =
  | 'interested'
  | 'applied'
  | 'interviewing'
  | 'rejected'
  | 'offer'
  | 'declined';

export type QueueJobStatus = 'applying' | 'queued' | 'submitted' | 'failed' | 'captcha';

export type SubscriptionStatus =
  | 'active'
  | 'inactive'
  | 'churned'
  | 'pending'
  | 'expired'
  | 'cancelled';

/** Status of a billing transaction (`billing_transactions.status`). */
export type TransactionStatus = 'paid' | 'pending' | 'failed';

/**
 * Units for a subscription plan's validity duration (`subscription_plans.duration_unit`).
 * Runtime source of truth — the `PlanDurationUnit` type, the Drizzle pgEnum, and
 * request validators all derive from this single array.
 */
export const PLAN_DURATION_UNITS = ['day', 'week', 'month', 'year'] as const;
export type PlanDurationUnit = (typeof PLAN_DURATION_UNITS)[number];

/**
 * Which evidence about the CUSTOMER drives their job match (`customers.match_source`).
 * Runtime source of truth — the `MatchSource` type, the Postgres `match_source_enum`,
 * the Drizzle mirror and the request validators all derive from this array.
 *
 * Switches capability evidence only — skills, experience, titles, and the semantic
 * embedding built from them. Job preferences (location, work mode, job type, salary)
 * are logistics and apply under every source; no resume makes an unreachable job
 * acceptable.
 */
export const MATCH_SOURCES = ['combined', 'profile', 'resume'] as const;
export type MatchSource = (typeof MATCH_SOURCES)[number];

/** Pre-existing behaviour (profile merged with resume) — the default for everyone. */
export const DEFAULT_MATCH_SOURCE: MatchSource = 'combined';

export const MATCH_SOURCE_LABELS: Record<MatchSource, string> = {
  combined: 'Profile + Resume',
  profile: 'Profile',
  resume: 'Resume',
};

/** Narrowing guard for values arriving from the DB, query strings, or JSON. */
export function isMatchSource(value: unknown): value is MatchSource {
  return typeof value === 'string' && (MATCH_SOURCES as readonly string[]).includes(value);
}

/**
 * Why a customer is closing their account. Shared so the web picker and the
 * server-side validator cannot drift. `found_a_job` is not churn — it belongs in
 * the success funnel. `prefer_not_to_say` is always offered: under the DPDP Act
 * withdrawing consent must be as easy as giving it, so the picker never gates.
 */
export const DELETION_REASONS = [
  'found_a_job',
  'not_relevant',
  'too_many_emails',
  'privacy',
  'duplicate_account',
  'taking_a_break',
  'other',
  'prefer_not_to_say',
] as const;
export type DeletionReason = (typeof DELETION_REASONS)[number];

export const DELETION_REASON_LABELS: Record<DeletionReason, string> = {
  found_a_job: 'I found a job',
  not_relevant: "The jobs weren't relevant to me",
  too_many_emails: 'Too many emails or notifications',
  privacy: 'Privacy concerns',
  duplicate_account: 'I have another account',
  taking_a_break: 'Taking a break from job hunting',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
};

/** Free text is collected only for this reason. */
export const DELETION_REASON_NEEDS_NOTE: DeletionReason = 'other';

/** Grace window to restore within: the API's env default, and the web's copy. */
export const ACCOUNT_DELETION_GRACE_DAYS = 30;

// Customer-specific enums
export enum CareerLevel {
  Entry = 'entry',
  Mid = 'mid',
  Senior = 'senior',
  Lead = 'lead',
  Executive = 'executive',
  CLevel = 'c_level',
}

export enum EducationLevel {
  HighSchool = 'high_school',
  Associate = 'associate',
  Bachelor = 'bachelor',
  Master = 'master',
  Phd = 'phd',
  Professional = 'professional',
}

export enum WorkAuthorization {
  Citizen = 'citizen',
  PermanentResident = 'permanent_resident',
  WorkVisa = 'work_visa',
  RequiresSponsorship = 'requires_sponsorship',
  StudentVisa = 'student_visa',
}

export enum AvailabilityStatus {
  Immediately = 'immediately',
  Within2Weeks = 'within_2_weeks',
  WithinMonth = 'within_month',
  Within3Months = 'within_3_months',
  Flexible = 'flexible',
}

export enum JobSearchStatus {
  ActivelyLooking = 'actively_looking',
  PassivelyLooking = 'passively_looking',
  NotLooking = 'not_looking',
  Hired = 'hired',
}

export enum SkillLevel {
  Beginner = 'beginner',
  Intermediate = 'intermediate',
  Advanced = 'advanced',
  Expert = 'expert',
}

// ---------------------------------------------------------------------------
// cNear-aligned profile sub-document enums
//
// These mirror cNear's Mongoose enums verbatim (member *values* are kept
// exactly as cNear defines them) so that a future cNear → oubo profile sync can
// copy values across without translation. They back the embedded JSONB sub-docs
// on `customers` (employment_history, certifications) and selected `customers` columns.
// ---------------------------------------------------------------------------

/** Whether an employment engagement was paid or unpaid. Stored on
 *  employment_history[].type; defaults to Unpaid. */
export enum EmploymentType {
  Paid = 'Paid',
  Unpaid = 'Unpaid',
}

/** cNear `EXPERIENCE_TYPE` — the nature of a work/internship engagement.
 *  Discriminates employment_history entries between the Work Experience
 *  (Full Time) and Internship sections. */
export enum ExperienceType {
  FullTime = 'Full Time',
  PartTime = 'Part Time',
  Internship = 'Internship',
  IndustrialTraining = 'Industrial Training',
  Freelance = 'Freelance',
  Contract = 'Contract',
  Volunteer = 'Volunteer',
  Apprenticeship = 'Apprenticeship',
}

/** Classification of an achievement / award. */
export enum AchievementCategory {
  Academic = 'Academic',
  Sports = 'Sports',
  Technical = 'Technical',
  Cultural = 'Cultural',
  Leadership = 'Leadership',
  CommunityService = 'Community Service',
  Research = 'Research',
  Innovation = 'Innovation',
  Other = 'Other',
}

/** cNear `CERTIFICATE_CATEGORIES` — classification of a certification. */
export enum CertificateCategory {
  GloballyRecognized = 'Globally Recognized Certification',
  IndustryRecognized = 'Industry Recognized Certification',
  AcademicInstitutional = 'Academic / Institutional Certification',
  OnlineCourse = 'Online Course Certification (MOOCs)',
  Government = 'Government Certification',
  ProfessionalLicense = 'Professional License / Exam Certification',
}

/** cNear `CERTIFICATION_LEVELS` — difficulty/seniority of a certification. */
export enum CertificationLevel {
  Beginner = 'Beginner',
  Intermediate = 'Intermediate',
  Advanced = 'Advanced',
  Professional = 'Professional',
  Expert = 'Expert',
}

/** cNear `GENDERS`. Backed by the Postgres `gender_enum` (see migration 040). */
export enum Gender {
  Male = 'Male',
  Female = 'Female',
  PreferNotToSay = 'Prefer not to say',
}

/**
 * Onboarding "where did you get to know about us" options. Stored as free text
 * (see migration 041) — `Others` lets the user specify their own value, so the
 * persisted string is not constrained to these members.
 */
export enum ReferralSource {
  Instagram = 'Insta',
  Friend = 'friend',
  ChatGPT = 'gpt',
  Claude = 'claude',
  Website = 'website',
  University = 'university',
  Facebook = 'facebook',
  Others = 'others',
}

/** cNear `COMPANY_TYPE_PREFERENCES` — candidate's preferred company types. */
export enum CompanyTypePreference {
  ProductBased = 'Product Based Companies',
  ServiceBased = 'Service Based Companies',
  Startups = 'Startups',
  MNCs = 'MNCs',
  Consulting = 'Consulting Companies',
  GovernmentOrganizations = 'Government Organizations',
  PSU = 'PSU',
  ResearchOrganizations = 'Research Organizations',
  NGO = 'NGO / Non-Profit',
}

/**
 * cNear `STATUSES` — the candidate's standing. Named `ProfileStatus` to avoid
 * collision with the auth-level `UserStatus` (active/inactive), which is unrelated.
 */
export enum ProfileStatus {
  Alumni = 'Alumni',
  CurrentStudent = 'Current Student',
  WorkingProfessional = 'Working Professional',
  Other = 'Other',
}

/** cNear `GAP_YEARS` — the education/career transition a gap falls in. */
export enum GapType {
  TenthToTwelfth = '10th-12th',
  TenthToDiploma = '10th-Diploma',
  TwelfthToDiploma = '12th-Diploma',
  TwelfthToGraduation = '12th-Graduation',
  DiplomaToGraduation = 'Diploma-Graduation',
  GraduationToPostgraduation = 'Graduation-Postgraduation',
}

/** cNear `LANGUAGE_TYPES`. */
export enum LanguageType {
  Regional = 'Regional',
  Foreign = 'Foreign',
}

/** cNear `LANGUAGE_PROFICIENCY` (foreign languages). */
export enum LanguageProficiency {
  Beginner = 'Beginner',
  Intermediate = 'Intermediate',
  Advanced = 'Advanced',
  Proficient = 'Proficient',
}

/** cNear `WORK_TYPE_PREFERENCES`. */
export enum WorkTypePreference {
  Onsite = 'Onsite',
  Remote = 'Remote',
  Hybrid = 'Hybrid',
  NoPreference = 'No Preference',
}

/** cNear `SALARY_RANGES`. Named `ExpectedSalaryRange` to avoid collision with the
 *  job-side `SalaryRange` interface (min/max numeric range). */
export enum ExpectedSalaryRange {
  Below4Lpa = 'Below 4 LPA',
  Lpa4To6 = '4 - 6 LPA',
  Lpa6To8 = '6 - 8 LPA',
  Lpa8To12 = '8 - 12 LPA',
  Lpa12To20 = '12 - 20 LPA',
  Above20Lpa = '20+ LPA',
  NoPreference = 'No Preference',
}

/**
 * cNear `INDUSTRY_PREFERENCES`. Modeled as a const tuple + union (rather than a
 * ~150-member enum) since this large, dropdown-backing value set is consumed as
 * data far more often than by individual member reference. Values are verbatim.
 */
export const INDUSTRY_PREFERENCES = [
  'Accounting',
  'Airlines/Aviation',
  'Alternative Dispute Resolution',
  'Alternative Medicine',
  'Animation',
  'Apparel/Fashion',
  'Architecture/Planning',
  'Arts/Crafts',
  'Automotive',
  'Aviation/Aerospace',
  'Banking/Mortgage',
  'Biotechnology/Greentech',
  'Broadcast Media',
  'Building Materials',
  'Business Supplies/Equipment',
  'Capital Markets/Hedge Fund/Private Equity',
  'Chemicals',
  'Civic/Social Organization',
  'Civil Engineering',
  'Commercial Real Estate',
  'Computer Games',
  'Computer Hardware',
  'Computer Networking',
  'Computer Software/Engineering',
  'Computer/Network Security',
  'Construction',
  'Consumer Electronics',
  'Consumer Goods',
  'Consumer Services',
  'Cosmetics',
  'Dairy',
  'Defense/Space',
  'Design',
  'E-Learning',
  'Education Management',
  'Electrical/Electronic Manufacturing',
  'Entertainment/Movie Production',
  'Environmental Services',
  'Events Services',
  'Executive Office',
  'Facilities Services',
  'Farming',
  'Financial Services',
  'Fine Art',
  'Fishery',
  'Food Production',
  'Food/Beverages',
  'Fundraising',
  'Furniture',
  'Gambling/Casinos',
  'Glass/Ceramics/Concrete',
  'Government Administration',
  'Government Relations',
  'Graphic Design/Web Design',
  'Health/Fitness',
  'Higher Education/Acadamia',
  'Hospital/Health Care',
  'Hospitality',
  'Human Resources/HR',
  'Import/Export',
  'Individual/Family Services',
  'Industrial Automation',
  'Information Services',
  'Information Technology/IT',
  'Insurance',
  'International Affairs',
  'International Trade/Development',
  'Internet',
  'Investment Banking/Venture',
  'Investment Management/Hedge Fund/Private Equity',
  'Judiciary',
  'Law Enforcement',
  'Law Practice/Law Firms',
  'Legal Services',
  'Legislative Office',
  'Leisure/Travel',
  'Library',
  'Logistics/Procurement',
  'Luxury Goods/Jewelry',
  'Machinery',
  'Management Consulting',
  'Maritime',
  'Market Research',
  'Marketing/Advertising/Sales',
  'Mechanical or Industrial Engineering',
  'Media Production',
  'Medical Equipment',
  'Medical Practice',
  'Mental Health Care',
  'Military Industry',
  'Mining/Metals',
  'Motion Pictures/Film',
  'Museums/Institutions',
  'Music',
  'Nanotechnology',
  'Newspapers/Journalism',
  'Non-Profit/Volunteering',
  'Oil/Energy/Solar/Greentech',
  'Online Publishing',
  'Other Industry',
  'Outsourcing/Offshoring',
  'Package/Freight Delivery',
  'Packaging/Containers',
  'Paper/Forest Products',
  'Performing Arts',
  'Pharmaceuticals',
  'Philanthropy',
  'Photography',
  'Plastics',
  'Political Organization',
  'Primary/Secondary Education',
  'Printing',
  'Professional Training',
  'Program Development',
  'Public Relations/PR',
  'Public Safety',
  'Publishing Industry',
  'Railroad Manufacture',
  'Ranching',
  'Real Estate/Mortgage',
  'Recreational Facilities/Services',
  'Religious Institutions',
  'Renewables/Environment',
  'Research Industry',
  'Restaurants',
  'Retail Industry',
  'Security/Investigations',
  'Semiconductors',
  'Shipbuilding',
  'Sporting Goods',
  'Sports',
  'Staffing/Recruiting',
  'Supermarkets',
  'Telecommunications',
  'Textiles',
  'Think Tanks',
  'Tobacco',
  'Translation/Localization',
  'Transportation',
  'Utilities',
  'Venture Capital/VC',
  'Veterinary',
  'Warehousing',
  'Wholesale',
  'Wine/Spirits',
  'Wireless',
  'Writing/Editing',
] as const;

export type IndustryPreference = (typeof INDUSTRY_PREFERENCES)[number];

export const SKILL_LEVEL_COLORS: Record<SkillLevel, string> = {
  [SkillLevel.Expert]: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400',
  [SkillLevel.Advanced]: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400',
  [SkillLevel.Intermediate]: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400',
  [SkillLevel.Beginner]: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400',
};

export const WORK_MODE_COLORS: Record<WorkMode, string> = {
  [WorkMode.Remote]: '#6C6FFF',
  [WorkMode.Hybrid]: '#8B5CF6',
  [WorkMode.Onsite]: '#29D994',
};

export const WORK_MODE_BADGE_COLORS: Record<WorkMode, string> = {
  [WorkMode.Remote]: 'bg-[#6C6FFF]/10 text-[#6C6FFF]',
  [WorkMode.Hybrid]: 'bg-[#8B5CF6]/10 text-[#8B5CF6]',
  [WorkMode.Onsite]: 'bg-[#29D994]/10 text-[#29D994]',
};

export const WORK_MODE_DETAIL_COLORS: Record<WorkMode, string> = {
  [WorkMode.Remote]: 'bg-[#6C6FFF]/10 text-[#6C6FFF] border-[#6C6FFF]/20',
  [WorkMode.Hybrid]: 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20',
  [WorkMode.Onsite]: 'bg-[#29D994]/10 text-[#29D994] border-[#29D994]/20',
};

export const WORK_MODE_LABELS: Record<WorkMode, string> = {
  [WorkMode.Remote]: 'Remote',
  [WorkMode.Hybrid]: 'Hybrid',
  [WorkMode.Onsite]: 'Onsite',
};

export const WORK_MODE_CLASSES: Record<WorkMode, string> = {
  [WorkMode.Remote]: 'bg-blue-50 text-blue-700 border border-blue-200',
  [WorkMode.Hybrid]: 'bg-purple-50 text-purple-700 border border-purple-200',
  [WorkMode.Onsite]: 'bg-green-50 text-green-700 border border-green-200',
}
export const JOB_TYPE_COLORS: Record<JobType, string> = {
  [JobType.FullTime]: '#4169E1',
  [JobType.PartTime]: '#7A97ED',
  [JobType.Contract]: '#29D994',
  [JobType.Temporary]: '#F5A623',
  [JobType.Internship]: '#8B5CF6',
  [JobType.PerDiem]: '#EC4899',
  [JobType.Volunteer]: '#14B8A6',
};

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  [JobType.FullTime]: 'Full-time',
  [JobType.PartTime]: 'Part-time',
  [JobType.Contract]: 'Contract',
  [JobType.Temporary]: 'Temporary',
  [JobType.Internship]: 'Internship',
  [JobType.PerDiem]: 'Per Diem',
  [JobType.Volunteer]: 'Volunteer',
};

export const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
]

// Currency
export enum Currency {
  INR = 'INR',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  AUD = 'AUD',
  CAD = 'CAD',
  SGD = 'SGD',
}

export const DEFAULT_CURRENCY = Currency.INR

// Plans
export enum PlanTier {
  Free = 'Free',
  Pro = 'Pro',
  Enterprise = 'Enterprise',
}

export const PLAN_STYLES: Record<PlanTier, { color: string; bg: string }> = {
  [PlanTier.Free]:       { color: 'text-[var(--oubo-text3)]', bg: 'bg-[var(--oubo-surface2)]' },
  [PlanTier.Pro]:        { color: 'text-[#6C6FFF]',           bg: 'bg-[#6C6FFF]/10' },
  [PlanTier.Enterprise]: { color: 'text-[#D97706]',           bg: 'bg-[#D97706]/10' },
};

// Answer Bank
export enum AnswerCategory {
  Personal = 'Personal',
  WorkAuthorization = 'Work Authorization',
  Availability = 'Availability',
  Salary = 'Salary',
  Custom = 'Custom',
}

export enum ConfidenceLevel {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

export enum UrgencyLevel {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

export const ANSWER_CATEGORY_STYLES: Record<AnswerCategory, { bg: string; text: string }> = {
  [AnswerCategory.Personal]:           { bg: 'bg-[#6C6FFF]/10', text: 'text-[#6C6FFF]' },
  [AnswerCategory.WorkAuthorization]:  { bg: 'bg-[#29D994]/10', text: 'text-[#29D994]' },
  [AnswerCategory.Availability]:       { bg: 'bg-[#F5A623]/10', text: 'text-[#F5A623]' },
  [AnswerCategory.Salary]:             { bg: 'bg-[#EC4899]/10', text: 'text-[#EC4899]' },
  [AnswerCategory.Custom]:             { bg: 'bg-[#8B5CF6]/10', text: 'text-[#8B5CF6]' },
};

export const CONFIDENCE_CONFIG: Record<ConfidenceLevel, { label: string; dot: string; badge: string }> = {
  [ConfidenceLevel.High]:   { label: 'High',   dot: 'bg-[#29D994]', badge: 'bg-[#29D994]/10 text-[#29D994]' },
  [ConfidenceLevel.Medium]: { label: 'Medium', dot: 'bg-[#F5A623]', badge: 'bg-[#F5A623]/10 text-[#F5A623]' },
  [ConfidenceLevel.Low]:    { label: 'Low',    dot: 'bg-[#FF5C5C]', badge: 'bg-[#FF5C5C]/10 text-[#FF5C5C]' },
};

export const URGENCY_CONFIG: Record<UrgencyLevel, { label: string; badge: string }> = {
  [UrgencyLevel.High]:   { label: 'High',   badge: 'bg-[#FF5C5C]/10 text-[#FF5C5C] border-[#FF5C5C]/20' },
  [UrgencyLevel.Medium]: { label: 'Medium', badge: 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20' },
  [UrgencyLevel.Low]:    { label: 'Low',    badge: 'bg-[var(--oubo-surface2)] text-[var(--oubo-text3)] border-[var(--oubo-border)]' },
};


export const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending:   { label: 'Pending',   className: 'bg-[var(--oubo-warning)]/10 text-[var(--oubo-warning)] border-[var(--oubo-warning)]/20' },
  submitted: { label: 'Submitted', className: 'bg-[var(--oubo-accent)]/10 text-[var(--oubo-accent)] border-[var(--oubo-accent)]/20' },
  interview: { label: 'Interview', className: 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20' },
  accepted:  { label: 'Accepted',  className: 'bg-[var(--oubo-success)]/10 text-[var(--oubo-success)] border-[var(--oubo-success)]/20' },
  rejected:  { label: 'Rejected',  className: 'bg-[var(--oubo-danger)]/10 text-[var(--oubo-danger)] border-[var(--oubo-danger)]/20' },
  applying:  { label: 'Applying',  className: 'bg-[#6C6FFF]/10 text-[#6C6FFF] border-[#6C6FFF]/20' },
  queued:    { label: 'Queued',    className: 'bg-[var(--oubo-text3)]/10 text-[var(--oubo-text3)] border-[var(--oubo-text3)]/20' },
  failed:    { label: 'Failed',    className: 'bg-[var(--oubo-danger)]/10 text-[var(--oubo-danger)] border-[var(--oubo-danger)]/20' },
  captcha:   { label: 'Captcha',   className: 'bg-[var(--oubo-warning)]/10 text-[var(--oubo-warning)] border-[var(--oubo-warning)]/20' },
}